// Slicing for the dev revenue overview.
//
// The API sends rows — invoices, payments, and each customer's estimate for the
// month in progress — and everything on the page is computed from them here,
// so a filter or a click re-slices instantly and every widget agrees with every
// other. Two kinds of narrowing apply:
//
//   filters  company / plan / status, chosen in the filter row; they scope the
//            whole page.
//   focus    a month clicked on the chart or an aging bucket clicked on the
//            receivables widget; it drills the widgets below the chart and the
//            invoice table into that slice.
//
// A widget that is itself a selector ignores its own filter (the company
// leaderboard ignores the company filter, the plan mix ignores the plan filter)
// and highlights the selection instead, so it stays usable for changing it.

export const STATUS_OPTIONS = [
  { id: 'paid', label: 'Paid' },
  { id: 'open', label: 'Open' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'void', label: 'Void' },
];

// Overdue isn't stored on an invoice; it's an open one past its due date.
export const invoiceState = (invoice) =>
  invoice.status === 'open' ? (invoice.overdue ? 'overdue' : 'open') : invoice.status;

export const AGING_BUCKETS = [
  { id: 'current', label: 'Not yet due', max: 0 },
  { id: '1-30', label: '1–30 days late', max: 30 },
  { id: '31-60', label: '31–60 days late', max: 60 },
  { id: '61-90', label: '61–90 days late', max: 90 },
  { id: '90+', label: 'Over 90 days late', max: Infinity },
];

export const agingBucket = (invoice) => {
  if (invoice.status !== 'open' || invoice.balance_cents <= 0) return null;
  return AGING_BUCKETS.find((bucket) => invoice.days_overdue <= bucket.max).id;
};

export const BREAKDOWNS = {
  type: {
    label: 'Revenue type',
    series: [
      { id: 'subscription', label: 'Seats & platform' },
      { id: 'usage', label: 'Usage overage' },
      { id: 'guests', label: 'Guest submissions' },
    ],
    // Which series each part of a bill feeds.
    of: (components) => ({
      subscription: (components.platform || 0) + (components.seats || 0),
      usage: (components.submissions || 0) + (components.storage || 0),
      guests: components.guests || 0,
    }),
  },
  plan: {
    label: 'Plan',
    series: [
      { id: 'starter', label: 'Starter' },
      { id: 'pro', label: 'Pro' },
      { id: 'enterprise', label: 'Enterprise' },
    ],
    of: (components, plan) => ({
      [plan]: Object.values(components).reduce((sum, cents) => sum + cents, 0),
    }),
  },
};

export const EMPTY_FILTERS = { companies: [], plans: [], statuses: [] };
export const EMPTY_FOCUS = { month: null, bucket: null };

export const hasFilters = (filters) =>
  filters.companies.length > 0 || filters.plans.length > 0 || filters.statuses.length > 0;

const sum = (rows, pick) => rows.reduce((total, row) => total + pick(row), 0);
const monthOf = (isoDate) => String(isoDate).slice(0, 7);

const matches = (invoice, filters, skip = null) =>
  (skip === 'companies' || !filters.companies.length || filters.companies.includes(invoice.company_id)) &&
  (skip === 'plans' || !filters.plans.length || filters.plans.includes(invoice.plan)) &&
  (skip === 'statuses' || !filters.statuses.length || filters.statuses.includes(invoiceState(invoice)));

// A payment answers to the filters through the invoice it paid. Every open
// invoice is in the payload, so a payment whose invoice is missing from it paid
// one that has since been settled.
const paymentMatches = (payment, invoicesById, filters, skip = null) => {
  const invoice = invoicesById.get(payment.invoice_id) || {
    company_id: payment.company_id,
    plan: payment.plan,
    status: 'paid',
  };
  return matches(invoice, filters, skip);
};

const byId = (invoices) => new Map(invoices.map((invoice) => [invoice.id, invoice]));

const inRange = (data, month) => month >= data.range.start && month <= data.range.end;
const inPrevious = (data, month) => month >= data.range.previous_start && month < data.range.start;

// Companies whose month-in-progress estimate counts: billed this month (not on
// a trial) and inside the company and plan filters. Status filters describe
// invoices, which an estimate isn't yet, so any status filter hides them.
const accruingCompanies = (data, filters, skip = null) =>
  filters.statuses.length
    ? []
    : data.companies.filter(
        (company) =>
          company.estimate &&
          company.subscription?.state === 'active' &&
          (skip === 'companies' || !filters.companies.length || filters.companies.includes(company.id)) &&
          (skip === 'plans' || !filters.plans.length || filters.plans.includes(company.subscription.plan)),
      );

export const summarize = (data, filters) => {
  const live = data.invoices.filter((invoice) => invoice.status !== 'void' && matches(invoice, filters));
  const invoicesById = byId(data.invoices);
  const payments = data.payments.filter((p) => paymentMatches(p, invoicesById, filters));
  const open = live.filter((invoice) => invoice.status === 'open');
  const overdue = open.filter((invoice) => invoice.overdue);
  const accruing = accruingCompanies(data, filters);

  const billedRows = live.filter((i) => inRange(data, i.period));
  const billed = sum(billedRows, (i) => i.total_cents);
  return {
    billed,
    previousBilled: sum(live.filter((i) => inPrevious(data, i.period)), (i) => i.total_cents),
    collected: sum(payments.filter((p) => inRange(data, monthOf(p.received_on))), (p) => p.amount_cents),
    previousCollected: sum(payments.filter((p) => inPrevious(data, monthOf(p.received_on))), (p) => p.amount_cents),
    // How much of the range's own bills has been paid. Not cash over billed:
    // cash in the range includes earlier months' bills, which would push that
    // past 100%.
    paidShare: billed > 0 ? sum(billedRows, (i) => i.amount_paid_cents) / billed : null,
    outstanding: sum(open, (i) => i.balance_cents),
    openInvoices: open.length,
    overdue: sum(overdue, (i) => i.balance_cents),
    overdueCompanies: new Set(overdue.map((i) => i.company_id)).size,
    accruing: sum(accruing, (c) => c.estimate.total_cents),
    projected: sum(accruing, (c) => c.projected_total_cents || 0),
    accruingCompanies: accruing.length,
  };
};

// One column per month: what was billed for it, split by ``breakdown``, and the
// cash that came in during it. The month in progress has no invoices yet, so
// its column is the running estimate instead, flagged so the chart can mark it.
export const monthlySeries = (data, filters, breakdown) => {
  const split = BREAKDOWNS[breakdown];
  const invoicesById = byId(data.invoices);
  const invoices = data.invoices.filter((i) => i.status !== 'void' && matches(i, filters));
  const payments = data.payments.filter((p) => paymentMatches(p, invoicesById, filters));
  const accruing = accruingCompanies(data, filters);

  return data.range.months.map((month) => {
    const segments = Object.fromEntries(split.series.map((s) => [s.id, 0]));
    const add = (parts) => {
      for (const [id, cents] of Object.entries(parts)) segments[id] = (segments[id] || 0) + cents;
    };
    const estimate = month === data.period.month;
    if (estimate) {
      for (const company of accruing) add(split.of(company.estimate.components, company.subscription.plan));
    } else {
      for (const invoice of invoices) {
        if (invoice.period === month) add(split.of(invoice.components, invoice.plan));
      }
    }
    return {
      month,
      segments,
      total: Object.values(segments).reduce((total, cents) => total + cents, 0),
      collected: sum(payments.filter((p) => monthOf(p.received_on) === month), (p) => p.amount_cents),
      estimate,
    };
  });
};

// Invoices behind the detail widgets and the table: the range (or the focused
// month), plus anything still open from before it — money owed doesn't stop
// being owed when its month scrolls out of the range.
export const visibleInvoices = (data, filters, focus, skip = null) =>
  data.invoices.filter(
    (invoice) =>
      matches(invoice, filters, skip) &&
      (focus.month ? invoice.period === focus.month : inRange(data, invoice.period) || invoice.status === 'open') &&
      (!focus.bucket || agingBucket(invoice) === focus.bucket),
  );

const visiblePayments = (data, filters, focus, skip = null) => {
  const invoicesById = byId(data.invoices);
  if (focus.bucket) {
    const ids = new Set(visibleInvoices(data, filters, focus, skip).map((invoice) => invoice.id));
    return data.payments.filter((payment) => ids.has(payment.invoice_id));
  }
  return data.payments.filter(
    (payment) =>
      paymentMatches(payment, invoicesById, filters, skip) &&
      (focus.month ? monthOf(payment.received_on) === focus.month : inRange(data, monthOf(payment.received_on))),
  );
};

// Receivables by how late they are. Narrowed by the filters and a focused
// month, but never by the focused bucket: the other buckets stay on screen so
// the selected one reads as a part of the whole.
export const aging = (data, filters, focus) => {
  const open = visibleInvoices(data, filters, { month: focus.month, bucket: null }).filter(agingBucket);
  return AGING_BUCKETS.map((bucket) => {
    const rows = open.filter((invoice) => agingBucket(invoice) === bucket.id);
    return {
      ...bucket,
      cents: sum(rows, (i) => i.balance_cents),
      invoices: rows.length,
      companies: new Set(rows.map((i) => i.company_id)).size,
    };
  });
};

export const LEADERBOARD_METRICS = [
  { id: 'billed', label: 'Billed' },
  { id: 'collected', label: 'Collected' },
  { id: 'owed', label: 'Owed' },
];

export const companyLeaderboard = (data, filters, focus, metric) => {
  const totals = new Map();
  const add = (companyId, cents) => totals.set(companyId, (totals.get(companyId) || 0) + cents);
  if (metric === 'collected') {
    for (const payment of visiblePayments(data, filters, focus, 'companies')) add(payment.company_id, payment.amount_cents);
  } else {
    for (const invoice of visibleInvoices(data, filters, focus, 'companies')) {
      if (invoice.status === 'void') continue;
      if (metric === 'owed') add(invoice.company_id, invoice.balance_cents);
      else if (focus.month || inRange(data, invoice.period)) add(invoice.company_id, invoice.total_cents);
    }
  }
  const names = new Map(data.companies.map((company) => [company.id, company.name]));
  return [...totals.entries()]
    .filter(([, cents]) => cents > 0)
    .map(([id, cents]) => ({ id, name: names.get(id) || `Company #${id}`, cents }))
    .sort((a, b) => b.cents - a.cents || a.name.localeCompare(b.name));
};

export const planMix = (data, filters, focus) => {
  const rows = BREAKDOWNS.plan.series.map((plan) => ({ ...plan, cents: 0, companies: new Set() }));
  for (const invoice of visibleInvoices(data, filters, focus, 'plans')) {
    if (invoice.status === 'void' || !(focus.month || inRange(data, invoice.period))) continue;
    const row = rows.find((r) => r.id === invoice.plan);
    if (!row) continue;
    row.cents += invoice.total_cents;
    row.companies.add(invoice.company_id);
  }
  return rows.map((row) => ({ ...row, companies: row.companies.size }));
};

// The invoice table, sorted. ``query`` matches the number or the company.
export const SORTS = {
  period: (a, b) => a.period.localeCompare(b.period) || a.number.localeCompare(b.number),
  company: (a, b) => (a.company_name || '').localeCompare(b.company_name || '') || a.period.localeCompare(b.period),
  total: (a, b) => a.total_cents - b.total_cents,
  balance: (a, b) => a.balance_cents - b.balance_cents,
  due: (a, b) => a.due_on.localeCompare(b.due_on),
  status: (a, b) => invoiceState(a).localeCompare(invoiceState(b)) || b.days_overdue - a.days_overdue,
};

export const invoiceRows = (data, filters, focus, query, sort) => {
  const needle = query.trim().toLowerCase();
  const rows = visibleInvoices(data, filters, focus).filter(
    (invoice) =>
      !needle ||
      invoice.number.toLowerCase().includes(needle) ||
      (invoice.company_name || '').toLowerCase().includes(needle),
  );
  const compare = SORTS[sort.key] || SORTS.period;
  return rows.sort((a, b) => (sort.dir === 'asc' ? compare(a, b) : compare(b, a)));
};

// Ranges past two years are drawn by quarter: sixty monthly columns on a phone
// are a pixel wide each. A quarter carries the months it covers so a click on
// it can zoom the range in to them.
export const toQuarters = (months) => {
  const quarters = new Map();
  for (const entry of months) {
    const [year, month] = entry.month.split('-').map(Number);
    const key = `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
    const quarter = quarters.get(key) || { key, months: [], segments: {}, total: 0, collected: 0, estimate: false };
    quarter.months.push(entry.month);
    for (const [id, cents] of Object.entries(entry.segments)) quarter.segments[id] = (quarter.segments[id] || 0) + cents;
    quarter.total += entry.total;
    quarter.collected += entry.collected;
    quarter.estimate = quarter.estimate || entry.estimate;
    quarters.set(key, quarter);
  }
  return [...quarters.values()];
};
